import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  BookOpen, Brain, FileText, PenTool, Target, Monitor, Award,
  PlayCircle, Download, ChevronLeft, ChevronRight, Home, Loader2,
  Video, FileDown, HelpCircle, CheckCircle, XCircle, Play, Clock,
  BookMarked, GraduationCap, ListChecks, AlertTriangle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_academia-sabeneval/artifacts/ai0i1vi4_2F29CCC3-E812-45AF-8948-674464C4314C.PNG";

const MODULE_ICONS = { BookOpen, Brain, FileText, PenTool, Target, Monitor, Award, Heart: PlayCircle, Microscope: BookMarked };
const MODULE_COLORS = { purple: "bg-purple-600", blue: "bg-blue-600", green: "bg-green-600", orange: "bg-orange-600", red: "bg-red-600", teal: "bg-teal-600", gold: "bg-yellow-600", pink: "bg-pink-600", cyan: "bg-cyan-600" };

export default function ModuloDetalle() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("contenido");
  const [exerciseAnswers, setExerciseAnswers] = useState({});
  const [exerciseResults, setExerciseResults] = useState({});
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResults, setQuizResults] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [simulators, setSimulators] = useState([]);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const savedEmail = sessionStorage.getItem("studentEmail");
    if (!savedEmail) {
      navigate("/mi-curso");
      return;
    }
    fetchModule();
    if (parseInt(moduleId) === 6) {
      fetchSimulators();
    }
    // Load progress
    const savedProgress = localStorage.getItem(`module_${moduleId}_progress`);
    if (savedProgress) setProgress(parseInt(savedProgress));
  }, [moduleId, navigate]);

  const fetchModule = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/modules/${moduleId}/content`);
      setModule(response.data);
    } catch (error) {
      toast.error("Error al cargar el módulo");
    } finally {
      setLoading(false);
    }
  };

  const fetchSimulators = async () => {
    try {
      const response = await axios.get(`${API}/simulators`);
      setSimulators(response.data.simulators || []);
    } catch (error) {
      console.error("Error fetching simulators:", error);
    }
  };

  const checkAnswer = (exerciseId, correctOptionId) => {
    const selected = exerciseAnswers[exerciseId];
    if (!selected) { toast.error("Selecciona una respuesta"); return; }
    const isCorrect = selected === correctOptionId;
    setExerciseResults(prev => ({ ...prev, [exerciseId]: isCorrect }));
    if (isCorrect) toast.success("¡Respuesta correcta!");
    else toast.error("Respuesta incorrecta");
    updateProgress();
  };

  const submitQuiz = () => {
    const quiz = module?.content?.quiz || [];
    if (Object.keys(quizAnswers).length < quiz.length) {
      toast.error("Responde todas las preguntas");
      return;
    }
    const results = {};
    let correct = 0;
    quiz.forEach(q => {
      const isCorrect = quizAnswers[q.id] === q.correct_option_id;
      results[q.id] = isCorrect;
      if (isCorrect) correct++;
    });
    setQuizResults(results);
    setQuizSubmitted(true);
    toast.success(`Quiz completado: ${correct}/${quiz.length} correctas`);
    updateProgress(100);
  };

  const updateProgress = (value) => {
    const newProgress = value || Math.min(progress + 20, 100);
    setProgress(newProgress);
    localStorage.setItem(`module_${moduleId}_progress`, newProgress.toString());
  };

  const getVideoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${BACKEND_URL}${url}`;
  };

  const isLocalVideo = (url) => {
    return url && (url.includes("/uploads/") || url.endsWith(".mp4") || url.endsWith(".webm") || url.endsWith(".mov"));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  if (!module) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Módulo no encontrado</h2>
            <Button onClick={() => navigate("/mi-curso")} className="mt-4">Volver al curso</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const Icon = MODULE_ICONS[module.icon] || BookOpen;
  const colorClass = MODULE_COLORS[module.color] || MODULE_COLORS.purple;
  const content = module.content || { videos: [], texts: [], files: [], exercises: [], quiz: [], educational_content: "" };
  const isSimulatorModule = parseInt(moduleId) === 6;

  const tabs = [
    { id: "contenido", label: "Contenido", icon: BookMarked, count: content.educational_content ? 1 : 0 },
    { id: "videos", label: "Videos", icon: Video, count: content.videos?.length || 0 },
    { id: "ejercicios", label: "Ejercicios", icon: ListChecks, count: content.exercises?.length || 0 },
    { id: "quiz", label: "Mini Quiz", icon: HelpCircle, count: content.quiz?.length || 0 },
    { id: "archivos", label: "Archivos", icon: FileDown, count: content.files?.length || 0 },
  ];

  if (isSimulatorModule) {
    tabs.push({ id: "simuladores", label: "Simuladores", icon: Monitor, count: simulators.length });
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 py-4 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Sabeneval" className="h-10 w-auto" />
              <span className="font-heading font-bold text-lg text-purple-700 hidden sm:block">SABENEVAL</span>
            </a>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate("/mi-curso")} variant="outline" size="sm">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Mi Curso
            </Button>
            <Button onClick={() => navigate("/")} variant="ghost" size="sm">
              <Home className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Module Header */}
        <div className={`${colorClass} rounded-2xl p-6 text-white mb-6`}>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <Icon className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <Badge className="bg-white/20 text-white mb-2">Módulo {module.id}</Badge>
              <h1 className="font-heading text-2xl font-bold">{module.title}</h1>
              <p className="text-white/80 mt-1">{module.description}</p>
            </div>
          </div>
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-white/80">Progreso del módulo</span>
              <span className="font-semibold">{progress}%</span>
            </div>
            <Progress value={progress} className="h-2 bg-white/20" />
          </div>
        </div>

        {/* Main Content Card */}
        <Card className="overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id ? "text-purple-600 border-b-2 border-purple-600 bg-purple-50" : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.count > 0 && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === tab.id ? "bg-purple-100 text-purple-700" : "bg-slate-100"}`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* Contenido Educativo Tab */}
            {activeTab === "contenido" && (
              <div>
                {content.educational_content ? (
                  <div className="prose prose-slate max-w-none">
                    <div dangerouslySetInnerHTML={{ __html: content.educational_content }} />
                  </div>
                ) : (
                  <EmptyState icon={BookMarked} title="Contenido en desarrollo" description="El contenido educativo estará disponible pronto." />
                )}
              </div>
            )}

            {/* Videos Tab */}
            {activeTab === "videos" && (
              <div>
                {content.videos?.length > 0 ? (
                  <div className="space-y-6">
                    {content.videos.map((video) => (
                      <div key={video.id} className="border rounded-xl overflow-hidden">
                        <div className="aspect-video bg-slate-900">
                          {video.url ? (
                            isLocalVideo(video.url) ? (
                              <video
                                controls
                                className="w-full h-full"
                                src={getVideoUrl(video.url)}
                                preload="metadata"
                              >
                                Tu navegador no soporta el elemento de video.
                              </video>
                            ) : video.url.includes("youtube") || video.url.includes("vimeo") ? (
                              <iframe src={video.url} className="w-full h-full" frameBorder="0" allowFullScreen title={video.title} />
                            ) : (
                              <video controls className="w-full h-full" src={getVideoUrl(video.url)} preload="metadata">
                                Tu navegador no soporta el elemento de video.
                              </video>
                            )
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-400">
                              <PlayCircle className="w-16 h-16" />
                            </div>
                          )}
                        </div>
                        <div className="p-4 bg-white">
                          <h3 className="font-semibold">{video.title}</h3>
                          {video.original_filename && <p className="text-sm text-slate-500">{video.original_filename}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState icon={Video} title="Sin videos todavía" description="Los videos estarán disponibles pronto." />
                )}
              </div>
            )}

            {/* Ejercicios Tab */}
            {activeTab === "ejercicios" && (
              <div>
                {content.exercises?.length > 0 ? (
                  <div className="space-y-8">
                    {/* Header con información del ejercicio */}
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                      <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                        <ListChecks className="w-5 h-5" />
                        Ejercicios Prácticos - {content.exercises.length} preguntas
                      </h3>
                      <p className="text-blue-700 text-sm mt-1">Practica los conceptos del módulo con estos ejercicios</p>
                    </div>

                    {content.exercises.map((exercise, index) => (
                      <div key={`exercise-${exercise.id}-${index}`} className="border rounded-xl p-6">
                        <div className="flex items-start gap-4 mb-4">
                          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600 font-semibold">{index + 1}</div>
                          <h3 className="font-medium flex-1">{exercise.question}</h3>
                        </div>
                        <RadioGroup 
                          value={exerciseAnswers[exercise.id] || ""} 
                          onValueChange={(value) => setExerciseAnswers(prev => ({...prev, [exercise.id]: value}))} 
                          className="space-y-3 ml-12"
                        >
                          {exercise.options.map((option, optIdx) => (
                            <div key={`opt-${exercise.id}-${option.id}-${optIdx}`} className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${
                              exerciseResults[exercise.id] !== undefined 
                                ? option.id === exercise.correct_option_id 
                                  ? "bg-green-50 border-green-200" 
                                  : exerciseAnswers[exercise.id] === option.id 
                                    ? "bg-red-50 border-red-200" 
                                    : ""
                                : exerciseAnswers[exercise.id] === option.id 
                                  ? "bg-purple-50 border-purple-200" 
                                  : "hover:bg-slate-50"
                            }`}>
                              <RadioGroupItem value={option.id} id={`ex-${exercise.id}-${option.id}`} disabled={exerciseResults[exercise.id] !== undefined} />
                              <Label htmlFor={`ex-${exercise.id}-${option.id}`} className="flex-1 cursor-pointer">
                                <span className="font-medium mr-2">{option.id.toUpperCase()})</span>
                                {option.text}
                              </Label>
                              {exerciseResults[exercise.id] !== undefined && option.id === exercise.correct_option_id && (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              )}
                            </div>
                          ))}
                        </RadioGroup>
                        {exerciseResults[exercise.id] === undefined ? (
                          <Button onClick={() => checkAnswer(exercise.id, exercise.correct_option_id)} className="mt-4 ml-12 bg-purple-600 hover:bg-purple-700">
                            Verificar Respuesta
                          </Button>
                        ) : (
                          <div className={`mt-4 ml-12 p-3 rounded-lg ${exerciseResults[exercise.id] ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                            <p className="font-medium flex items-center gap-2">
                              {exerciseResults[exercise.id] ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                              {exerciseResults[exercise.id] ? "¡Correcto!" : "Incorrecto"}
                            </p>
                            {exercise.explanation && <p className="text-sm mt-1">{exercise.explanation}</p>}
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Resumen de ejercicios si todos están completados */}
                    {Object.keys(exerciseResults).length === content.exercises.length && content.exercises.length > 0 && (
                      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-6 mt-6">
                        <h3 className="font-bold text-lg text-purple-900 mb-3">📊 Resumen de Ejercicios</h3>
                        <div className="flex items-center gap-4 mb-4">
                          <div className="text-3xl font-bold text-purple-700">
                            {Object.values(exerciseResults).filter(Boolean).length}/{content.exercises.length}
                          </div>
                          <div className="text-purple-600">respuestas correctas</div>
                        </div>
                        <div className="bg-white/60 rounded-lg p-4">
                          {Object.values(exerciseResults).filter(Boolean).length < content.exercises.length ? (
                            <p className="text-purple-700 flex items-center gap-2">
                              <AlertTriangle className="w-5 h-5 text-amber-500" />
                              Te recomendamos repasar el contenido del módulo antes de continuar para reforzar los conceptos.
                            </p>
                          ) : (
                            <p className="text-green-700 flex items-center gap-2">
                              <CheckCircle className="w-5 h-5" />
                              ¡Excelente! Has dominado los ejercicios de este módulo. Continúa con el Mini Quiz.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <EmptyState icon={ListChecks} title="Sin ejercicios todavía" description="Los ejercicios estarán disponibles pronto." />
                )}
              </div>
            )}

            {/* Mini Quiz Tab */}
            {activeTab === "quiz" && (
              <div>
                {content.quiz?.length > 0 ? (
                  <div className="space-y-6">
                    <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 mb-6">
                      <h3 className="font-semibold text-purple-900 flex items-center gap-2">
                        <GraduationCap className="w-5 h-5" />
                        Mini Quiz - {content.quiz.length} preguntas
                      </h3>
                      <p className="text-purple-700 text-sm mt-1">Responde todas las preguntas y verifica tu aprendizaje. Solo una respuesta es correcta.</p>
                    </div>
                    
                    {content.quiz.map((q, index) => (
                      <div key={`quiz-q-${q.id}-${index}`} className="border rounded-xl p-6">
                        <div className="flex items-start gap-4 mb-4">
                          <div className="w-8 h-8 bg-purple-600 text-white rounded-lg flex items-center justify-center font-semibold">{index + 1}</div>
                          <h3 className="font-medium flex-1">{q.question}</h3>
                        </div>
                        <RadioGroup 
                          value={quizAnswers[q.id] || ""} 
                          onValueChange={(value) => setQuizAnswers(prev => ({...prev, [q.id]: value}))} 
                          className="space-y-3 ml-12"
                          disabled={quizSubmitted}
                        >
                          {q.options.map((option, optIdx) => (
                            <div key={`quiz-opt-${q.id}-${option.id}-${optIdx}`} className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${
                              quizSubmitted 
                                ? option.id === q.correct_option_id 
                                  ? "bg-green-50 border-green-200" 
                                  : quizAnswers[q.id] === option.id && option.id !== q.correct_option_id
                                    ? "bg-red-50 border-red-200" 
                                    : ""
                                : quizAnswers[q.id] === option.id 
                                  ? "bg-purple-50 border-purple-200" 
                                  : "hover:bg-slate-50"
                            }`}>
                              <RadioGroupItem value={option.id} id={`quiz-${q.id}-${option.id}`} disabled={quizSubmitted} />
                              <Label htmlFor={`quiz-${q.id}-${option.id}`} className="flex-1 cursor-pointer">
                                <span className="font-medium mr-2">{option.id.toUpperCase()})</span>
                                {option.text}
                              </Label>
                              {quizSubmitted && option.id === q.correct_option_id && (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              )}
                            </div>
                          ))}
                        </RadioGroup>
                        {quizSubmitted && q.explanation && (
                          <div className="mt-4 ml-12 p-3 rounded-lg bg-blue-50 text-blue-700">
                            <p className="text-sm"><strong>Explicación:</strong> {q.explanation}</p>
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {!quizSubmitted ? (
                      <Button onClick={submitQuiz} className="w-full bg-purple-600 hover:bg-purple-700 h-12 text-lg">
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Enviar Quiz
                      </Button>
                    ) : (
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
                        <div className="text-center mb-4">
                          <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
                          <h3 className="font-semibold text-green-900 text-lg">¡Quiz Completado!</h3>
                          <div className="text-3xl font-bold text-green-700 mt-2">
                            {Object.values(quizResults).filter(r => r).length}/{content.quiz.length}
                          </div>
                          <p className="text-green-600 mt-1">respuestas correctas</p>
                        </div>
                        
                        {/* Retroalimentación según el puntaje */}
                        <div className="bg-white/60 rounded-lg p-4 mb-4">
                          {Object.values(quizResults).filter(r => r).length >= content.quiz.length * 0.7 ? (
                            <p className="text-green-700">
                              ¡Excelente trabajo! Has demostrado un buen dominio del contenido. Continúa al siguiente módulo.
                            </p>
                          ) : Object.values(quizResults).filter(r => r).length >= content.quiz.length * 0.5 ? (
                            <p className="text-amber-700">
                              Buen intento. Te recomendamos repasar el contenido del módulo para reforzar algunos conceptos antes de continuar.
                            </p>
                          ) : (
                            <p className="text-red-700">
                              Te recomendamos repasar el contenido del módulo antes de continuar para reforzar los conceptos.
                            </p>
                          )}
                        </div>
                        
                        <Button onClick={() => { setQuizAnswers({}); setQuizResults({}); setQuizSubmitted(false); }} variant="outline" className="w-full">
                          Intentar de Nuevo
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <EmptyState icon={HelpCircle} title="Sin quiz todavía" description="El mini quiz estará disponible pronto." />
                )}
              </div>
            )}

            {/* Archivos Tab */}
            {activeTab === "archivos" && (
              <div>
                {content.files?.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    {content.files.map((file) => (
                      <a key={file.id} href={getVideoUrl(file.file_url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 border rounded-xl hover:border-purple-200 hover:bg-purple-50 transition-colors">
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                          <Download className="w-6 h-6 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">{file.title}</h4>
                          <p className="text-sm text-slate-500 uppercase">{file.file_type || "PDF"}</p>
                        </div>
                        <FileDown className="w-5 h-5 text-slate-400" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <EmptyState icon={FileDown} title="Sin archivos todavía" description="Los archivos estarán disponibles pronto." />
                )}
              </div>
            )}

            {/* Simuladores Tab */}
            {activeTab === "simuladores" && isSimulatorModule && (
              <div>
                {simulators.length > 0 ? (
                  <div className="space-y-4">
                    <div className="bg-teal-50 border border-teal-100 rounded-xl p-4 mb-6">
                      <h3 className="font-semibold text-teal-900">Simuladores EXANI II</h3>
                      <p className="text-teal-700 text-sm mt-1">Practica con exámenes completos tipo EXANI II</p>
                    </div>
                    {simulators.map((sim) => (
                      <Card key={sim.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                                <Monitor className="w-6 h-6 text-teal-600" />
                              </div>
                              <div>
                                <h3 className="font-semibold">{sim.title}</h3>
                                <p className="text-sm text-slate-500">{sim.questions?.length || 30} preguntas • {sim.time_minutes || 60} minutos</p>
                              </div>
                            </div>
                            <Button onClick={() => navigate(`/mi-curso/simulador/${sim.id}`)} className="bg-teal-600 hover:bg-teal-700">
                              <Play className="w-4 h-4 mr-2" />
                              Iniciar
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <EmptyState icon={Monitor} title="Cargando simuladores..." description="Los simuladores estarán disponibles pronto." />
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <Button
            onClick={() => navigate(`/mi-curso/modulo/${parseInt(moduleId) - 1}`)}
            disabled={parseInt(moduleId) <= 1}
            variant="outline"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Módulo Anterior
          </Button>
          <Button
            onClick={() => navigate(`/mi-curso/modulo/${parseInt(moduleId) + 1}`)}
            disabled={parseInt(moduleId) >= 7}
            className="bg-purple-600 hover:bg-purple-700"
          >
            Siguiente Módulo
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, description }) {
  return (
    <div className="text-center py-12">
      <Icon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
      <h3 className="font-semibold text-slate-900">{title}</h3>
      <p className="text-sm text-slate-500">{description}</p>
    </div>
  );
}
